import graphql__ab2d25a7e26ff2195789 from "./__generated__/AnalysisForecastAccountsViewQuery.graphql.ts";import * as React from "react";
import {startTransition, useState} from "react";
import {Link, useOutletContext} from "react-router";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faUpload} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import Hint from "./presentational/Hint";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import UploadBankStatementDialog from "./dialog/UploadBankStatementDialog";
import {formatAmount} from "../helper/revenueFormat";
import AnalysisForecastAccountsViewQueryNode from "./__generated__/AnalysisForecastAccountsViewQuery.graphql";
import type {AnalysisForecastAccountsViewQuery as AnalysisForecastAccountsViewQueryType} from "./__generated__/AnalysisForecastAccountsViewQuery.graphql";
import {formatDay} from "../helper/dateFormat";

const analysisForecastAccountsViewQuery = graphql__ab2d25a7e26ff2195789;

type AnalysisOutletContext = {
    bankAccountsQuery: any
};

type BankAccountRow = {
    readonly bankAccountIdentifier: string
    readonly name: string
};

type OpenDialog = {bankAccount: BankAccountRow} | null;

/**
 * Lists the bank accounts with the day their statements cover, the last upload and the balance
 * computed from them. Statements are uploaded per account. Accounts are created and edited in
 * the configuration.
 */
const AnalysisForecastAccountsView: React.FC = () => {
    const intl = useIntl();
    const {bankAccountsQuery} = useOutletContext<AnalysisOutletContext>();
    const [queryRef, loadBankAccountsQuery] = useQueryLoader<AnalysisForecastAccountsViewQueryType>(AnalysisForecastAccountsViewQueryNode, bankAccountsQuery);
    const data = usePreloadedQuery<AnalysisForecastAccountsViewQueryType>(analysisForecastAccountsViewQuery, queryRef ?? bankAccountsQuery);
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

    const bankAccounts = data.bankAccounts;

    const reloadBankAccounts = () => {
        startTransition(() => {
            loadBankAccountsQuery({}, {fetchPolicy: 'network-only'});
        });
    };

    const closeDialog = (changed: boolean) => {
        setOpenDialog(null);
        if (changed) {
            reloadBankAccounts();
        }
    };

    return (
        <>
            <Panel
                headline={intl.formatMessage({id: 'view.analysisForecastAccounts.headline'})}
                fullWidthChildren={true}
            >
                {bankAccounts.length === 0 ? (
                    <Hint className="px-4 py-5 sm:p-6">
                        {intl.formatMessage(
                            {id: 'view.analysisForecastAccounts.empty'},
                            {link: (chunks) => <Link key="finance" className="text-blue-600 hover:underline" to="/configuration/finance">{chunks}</Link>}
                        )}
                    </Hint>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.analysisForecastAccounts.col.name'})}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastAccounts.col.currentBalance'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastAccounts.col.coveredUntil'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastAccounts.col.lastUpload'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {bankAccounts.map((bankAccount, index) => (
                                    <tr key={bankAccount.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData className="pl-4 sm:pl-6 pr-3">
                                            <span className="font-medium text-gray-900">{bankAccount.name}</span>
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap font-medium" textAlignment={TextAlignment.Right}>
                                            {formatAmount(bankAccount.currentBalance)}
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap">
                                            {bankAccount.coveredUntil ? formatDay(intl, bankAccount.coveredUntil) : <span className="text-gray-300">–</span>}
                                        </TableData>
                                        <TableData className="px-3">
                                            {bankAccount.lastUpload ? (
                                                <>
                                                    <div className="whitespace-nowrap">
                                                        {intl.formatDate(bankAccount.lastUpload.uploadedAt, {dateStyle: 'medium', timeStyle: 'short'})}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {intl.formatMessage(
                                                            {id: 'view.analysisForecastAccounts.lastUpload.details'},
                                                            {
                                                                count: bankAccount.lastUpload.bookingCount,
                                                                start: formatDay(intl, bankAccount.lastUpload.firstBookingDate),
                                                                end: formatDay(intl, bankAccount.lastUpload.lastBookingDate),
                                                                user: bankAccount.lastUpload.uploadedBy,
                                                            }
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastAccounts.noUpload'})}</span>
                                            )}
                                        </TableData>
                                        <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            <ActionButton
                                                icon={faUpload}
                                                title={intl.formatMessage({id: 'view.analysisForecastAccounts.action.upload'})}
                                                onClick={() => setOpenDialog({bankAccount})}
                                            />
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>

            {openDialog && (
                <UploadBankStatementDialog bankAccount={openDialog.bankAccount} onClose={closeDialog}/>
            )}
        </>
    );
};

export default AnalysisForecastAccountsView;
